import { Fragment, useState, useEffect, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import CreditRefundDropDown from "../dropdown/CreditRefundDropdown";
import IconButton from "../iconButton";
import { useSaveCreditsRefundMutation } from "../../store/creditRefund";
import { toast } from "react-toastify";
import CreditRefundSuccessSlideOver from "./CreditRefundSuccessSlideOver";

const CreditSlideOver = ({
  show,
  setShowSlide,
  leadMatchesData,
  handleClose,
}) => {
  const refund = [
    { id: 1, label: "Missing or wrong contact details" },
    { id: 2, label: "Duplicate match" },
    { id: 3, label: "I do not serve this location" },
    { id: 4, label: "I do not perform this service" },
    { id: 5, label: "Other" }
  ];

  const reasonRef = useRef();
  const [selectedLead, setSelectedLead] = useState({});
  const [selectedOption, setSelectedOption] = useState(refund[0]);
  const [otherReason, setOtherReason] = useState("");
  const [showCreditSuccess, setShowCreditSuccess] = useState(false);

  const [
    saveCreditRefundDetails,
    {
      isSuccess: creditRefundSuccess,
      isLoading: creditRefundLoader,
      isError: creditRefundIsError,
      error: creditRefundError,
      data: creditRefundData,
    },
  ] = useSaveCreditsRefundMutation();

  useEffect(() => {
    if (leadMatchesData) {
      setSelectedLead(leadMatchesData[0]);
    }
  }, [leadMatchesData]);

  useEffect(() => {
    if (creditRefundSuccess && !creditRefundLoader) {
      setShowCreditSuccess(true);
    }
  }, [creditRefundSuccess]);

  useEffect(() => {
    if (creditRefundIsError && !creditRefundLoader) {
      setShowCreditSuccess(true);
    }
  }, [creditRefundIsError]);

  const handleRadioChange = (option) => {
    setSelectedOption(option);
  };

  const handleCreditRefundSubmit = () => {
    if (selectedOption.id == 5 && otherReason === "") {
      toast.error("The reason field is required.");
      reasonRef.current.focus();
      reasonRef.current.style.border = "2px solid red";
      return;
    }
    const params = {
      reason: selectedOption.id == 5 ? otherReason : selectedOption.label,
      lead_user_id: selectedLead?.match_id,
    };
    saveCreditRefundDetails(params);
  };

  return (
    <>
      <Transition.Root show={show} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full px-[15px] py-5 md:p-0">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-500 sm:duration-700"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-500 sm:duration-700"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto w-screen max-w-[478px]">
                    {!showCreditSuccess && (
                      <div className="flex h-full flex-col bg-white py-5 md:py-10 px-4 md:px-8 shadow-xl rounded-md md:rounded-0">
                        <div className="">
                          <div className="border-b border-solid border-[#CCD0D4] mb-[14px] md:mb-5 pb-[14px] md:pb-[30px]">
                            <Dialog.Title className="text-xl md:text-2xl font-bold leading-[160%] text-[#04091E]">
                              New Credit Refund Request
                            </Dialog.Title>
                          </div>
                        </div>
                        <form>
                          <div>
                            <h6 className="text-sm md:text-lg font-bold leading-[160%] text-[#485868] mb-3">
                              Select Lead
                            </h6>
                            <CreditRefundDropDown
                              matchesData={leadMatchesData}
                              classes="px-[14px] py-3 border w-full border-solid border-[#EBEFF3] rounded-[10px] mb-5 h-[60px] md:h-[70px] bg-[#FFFFFF] flex items-center justify-between"
                              textClasses="w-full text-left gap-x-[10px] text-lg font-medium text-customGray"
                              rightIconClasses="-mr-1 h-6 w-6 text-[#485868] font-bold"
                              menuClasses="absolute left-0 z-10 mt-[10px] w-full origin-top rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                              selectedLead={selectedLead}
                              setSelectedLead={setSelectedLead}
                            />
                          </div>
                          <div className="credit-list scroller max-h-[calc(100vh-420px)] overflow-y-auto pr-1">
                            <h6 className="text-sm md:text-lg font-bold leading-[160%] text-[#485868] mb-3">
                              Please select a reason for submitting your credit
                              refund request:
                            </h6>
                            <div className="form-group3">
                              <ul>
                                {refund &&
                                  refund.map((item, index) => (
                                    <li
                                      key={item.id}
                                      onClick={() => handleRadioChange(item)}
                                      className={`flex items-center gap-[12px] border border-solid border-[#CCD0D4] rounded-[10px] p-3 md:p-5 pl-4 mb-3 cursor-pointer ${
                                        selectedOption.id == item.id
                                          ? "border-primary border-[1.5px]"
                                          : "border-[#CCD0D4]"
                                      } ${index === 0 ? "checked" : ""}`}
                                    >
                                      <div className="relative">
                                        <input
                                          type="radio"
                                          id={`radio-${item.id}-${index}`}
                                          name="credit-options"
                                          className="bg-white"
                                          checked={selectedOption.id == item.id}
                                          onChange={() =>
                                            handleRadioChange(item)
                                          }
                                        />
                                        <label
                                          htmlFor={`radio-${item.id}-${index}`}
                                          className="text-sm md:text-lg font-medium leading-[160%] text-customGray"
                                        >
                                          {item.label}
                                        </label>
                                        <div className="check"></div>
                                      </div>
                                    </li>
                                  ))}
                                {selectedOption.id == 5 && (
                                  <li>
                                    <textarea
                                      ref={reasonRef}
                                      name="reason"
                                      className="text-sm md:text-base leading-[160%] text-customGray px-[14px] md:px-5 py-5 pb-6 border border-solid border-strokeColor rounded-[10px] w-full outline-none"
                                      value={otherReason}
                                      onChange={(e) => {
                                        if (e.target.value == "") {
                                          reasonRef.current.style.border =
                                            "2px solid red";
                                        } else {
                                          reasonRef.current.style.border =
                                            "1px solid #B7C9DA";
                                        }
                                        setOtherReason(e.target.value);
                                      }}
                                      placeholder="Please explain your reason. Your detailed explanation will help us in the review process..."
                                    />
                                  </li>
                                )}
                              </ul>
                            </div>
                          </div>
                        </form>
                        <div className="grid grid-cols-2 gap-5 mt-[30px]">
                          <button
                            type="button"
                            className="rounded-[10px] border border-solid border-[#EBEFF3] bg-[#EBEFF3] text-sm md:text-base font-black uppercase text-[#485868] flex justify-center items-center gap-[6px] w-full h-10 md:h-[58px]"
                            onClick={() => handleClose()}
                          >
                            <XCircleIcon className="w-6 h-6 text-[#485868]" />
                            CANCEL
                          </button>
                          <IconButton
                            classes="rounded-[10px] bg-primary text-sm md:text-base font-black uppercase text-white px-3 flex justify-center items-center gap-[6px] w-full mb-4 h-10 md:h-[58px]"
                            icon={CheckCircleIcon}
                            iconClasses="w-6 h-6 text-white"
                            title="SUBMIT"
                            onClick={handleCreditRefundSubmit}
                            buttonLoader={creditRefundLoader}
                          />
                        </div>
                      </div>
                    )}
                    {showCreditSuccess && (
                      <CreditRefundSuccessSlideOver
                        handleClose={() => {
                          handleClose();
                          setSelectedOption(refund[0]);
                          setShowCreditSuccess(false);
                        }}
                        isError={creditRefundError ? true : false}
                        error={
                          creditRefundError?.data?.error ||
                          creditRefundError?.data?.lead_user_id[0]
                        }
                      />
                    )}
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default CreditSlideOver;
